'use client';

/**
 * WelcomeModal — primul ecran al turului ghidat.
 *
 * Aestetic: editorial/refined. Bej cald + accente amber + acente verde subtil.
 * Tipografie: Instrument Serif pentru headline, mono pentru numerotare.
 * Decorative: două orburi gradient în colțuri (top-right amber, bottom-left emerald) + grain subtle.
 *
 * Comportament:
 *  - Backdrop click → onSkip (cu blur subtil)
 *  - ESC → onSkip
 *  - X button → onSkip
 *  - "Începe turul ghidat" → onStart (CTA primary, amber)
 *  - "Explorează singur" → onSkip (CTA secondary, ghost)
 *
 * A11y:
 *  - role=dialog, aria-modal, aria-labelledby + aria-describedby
 *  - autofocus pe primary CTA (deschis automat la prima vizită — flow nou,
 *    fără trigger anterior la care să returnăm focusul)
 *  - body scroll lock cât timp e deschis
 */

import { useEffect, useRef } from 'react';
import {
  X,
  Sparkles,
  Receipt,
  Users,
  Package,
  ListChecks,
  ArrowRight,
} from 'lucide-react';

interface WelcomeModalProps {
  open: boolean;
  onStart: () => void;
  onSkip: () => void;
  userName?: string;
}

const PREVIEW_ITEMS: Array<{
  icon: typeof Receipt;
  label: string;
  num: string;
}> = [
  { icon: Receipt, label: 'Facturi', num: '01' },
  { icon: Users, label: 'Clienți', num: '02' },
  { icon: Package, label: 'Produse', num: '03' },
  { icon: ListChecks, label: 'Configurare', num: '04' },
];

export function WelcomeModal({
  open,
  onStart,
  onSkip,
  userName,
}: WelcomeModalProps) {
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onSkip();
    }
    document.addEventListener('keydown', onEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Defer focus so the entry animation has time to start
    const t = window.setTimeout(() => primaryRef.current?.focus(), 80);
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
    };
  }, [open, onSkip]);

  if (!open) return null;

  const greeting = userName ? `Bun venit, ${userName}!` : 'Bun venit!';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sf-welcome-title"
      aria-describedby="sf-welcome-desc"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Închide"
        onClick={onSkip}
        className="absolute inset-0 cursor-default bg-neutral-950/55 dark:bg-black/70 backdrop-blur-[3px] sf-welcome-fade"
      />

      {/* Modal card */}
      <div className="relative w-full max-w-[540px] overflow-hidden rounded-2xl bg-[#FAF8F4] dark:bg-neutral-950 ring-1 ring-neutral-200/80 dark:ring-neutral-800 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.25),0_8px_16px_-8px_rgba(15,23,42,0.15)] sf-welcome-in origin-center">
        {/* Decorative gradient orb — top-right, bleeds out */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle at center, rgba(251,191,36,0.45) 0%, rgba(245,158,11,0.18) 40%, transparent 70%)',
          }}
        />

        {/* Decorative gradient orb — bottom-left, subtle emerald */}
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-16 h-48 w-48 rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle at center, rgba(16,185,129,0.22) 0%, rgba(5,150,105,0.08) 40%, transparent 70%)',
          }}
        />

        {/* Subtle grain overlay */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.025] dark:opacity-[0.045] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
          }}
        />

        {/* Close button */}
        <button
          type="button"
          onClick={onSkip}
          aria-label="Închide modalul"
          className="absolute right-3.5 top-3.5 z-10 grid h-8 w-8 place-items-center rounded-full text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100/80 dark:hover:bg-neutral-900/80 transition-colors"
        >
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>

        {/* Content */}
        <div className="relative px-7 pt-9 pb-7 sm:px-9 sm:pt-10 sm:pb-8">
          {/* Eyebrow */}
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50/80 dark:bg-amber-950/40 ring-1 ring-amber-200/80 dark:ring-amber-800/60 px-2.5 py-1">
            <Sparkles
              className="h-3 w-3 text-amber-600 dark:text-amber-400"
              strokeWidth={2.4}
            />
            <span
              className="text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-400 tabular-nums"
              style={{
                fontFamily:
                  '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
              }}
            >
              Tur ghidat · ~60 sec
            </span>
          </div>

          {/* Headline — serif */}
          <h2
            id="sf-welcome-title"
            className="text-[1.75rem] sm:text-[2.125rem] leading-[1.05] tracking-[-0.02em] text-neutral-900 dark:text-neutral-50"
            style={{
              fontFamily: '"Instrument Serif", "Cormorant Garamond", Georgia, serif',
              fontWeight: 400,
            }}
          >
            {greeting}
          </h2>

          {/* Subline */}
          <p
            id="sf-welcome-desc"
            className="mt-3 text-[0.9063rem] leading-[1.55] text-neutral-600 dark:text-neutral-400 max-w-[440px]"
          >
            Hai să-ți arătăm rapid unde sunt cele mai folosite funcții — facturi,
            clienți, produse și pașii rămași pentru configurare.{' '}
            <span className="text-neutral-800 dark:text-neutral-200 font-medium">
              Durează mai puțin de un minut.
            </span>
          </p>

          {/* Preview chips — 4 numbered cards */}
          <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {PREVIEW_ITEMS.map(({ icon: Icon, label, num }, i) => (
              <div
                key={label}
                className="sf-welcome-stagger group relative flex flex-col items-start gap-2 rounded-xl bg-white/70 dark:bg-neutral-900/50 ring-1 ring-neutral-200/80 dark:ring-neutral-800/80 px-3 py-2.5 transition-all duration-200 hover:bg-white dark:hover:bg-neutral-900 hover:ring-amber-400/70 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-4px_rgba(245,158,11,0.25)]"
                style={{ animationDelay: `${140 + i * 70}ms` }}
              >
                <div className="flex w-full items-center justify-between">
                  <div className="grid h-7 w-7 place-items-center rounded-lg bg-amber-50 dark:bg-amber-950/40 ring-1 ring-amber-200/70 dark:ring-amber-800/60 text-amber-700 dark:text-amber-400">
                    <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
                  </div>
                  <span
                    className="text-[0.625rem] font-semibold tabular-nums text-neutral-400 dark:text-neutral-600 tracking-wider"
                    style={{
                      fontFamily:
                        '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
                    }}
                  >
                    {num}
                  </span>
                </div>
                <div className="text-[0.7813rem] font-semibold text-neutral-800 dark:text-neutral-200">
                  {label}
                </div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-7 flex flex-col-reverse sm:flex-row sm:items-center gap-2.5">
            <button
              type="button"
              onClick={onSkip}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-[0.8125rem] font-semibold text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100/80 dark:hover:bg-neutral-900/80 transition-colors"
            >
              Explorează singur
            </button>
            <button
              ref={primaryRef}
              type="button"
              onClick={onStart}
              className="group inline-flex flex-1 sm:flex-initial items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-600 active:bg-amber-700 px-5 py-2.5 text-[0.8125rem] font-semibold text-white shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_20px_-6px_rgba(245,158,11,0.55)] hover:shadow-[0_1px_2px_rgba(0,0,0,0.08),0_12px_24px_-6px_rgba(245,158,11,0.7)] transition-all"
            >
              Începe turul ghidat
              <ArrowRight
                className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.4}
              />
            </button>
          </div>

          {/* Caption */}
          <p className="mt-4 text-[0.6875rem] text-neutral-500 dark:text-neutral-500 text-center sm:text-left">
            Poți relua oricând turul din meniul{' '}
            <span className="font-medium text-neutral-700 dark:text-neutral-300">
              Ajutor
            </span>{' '}
            (top-bar)
          </p>
        </div>
      </div>

      {/* Animations — React 19 hoists <style> tags to <head> */}
      <style>{`
        @keyframes sfWelcomeFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes sfWelcomeIn {
          from { opacity: 0; transform: scale(0.96) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes sfWelcomeStagger {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .sf-welcome-fade {
          animation: sfWelcomeFadeIn 220ms ease-out forwards;
        }
        .sf-welcome-in {
          animation: sfWelcomeIn 340ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .sf-welcome-stagger {
          opacity: 0;
          animation: sfWelcomeStagger 460ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
      `}</style>
    </div>
  );
}
